import {
  Upload as AntdUpload,
  type GetProp,
  type UploadFile,
  type UploadProps,
  message,
} from 'antd'
import { useLingui } from '@lingui/react/macro'

import type { UploadChangeParam } from 'antd/es/upload'
import type { IFileResponse } from '@/pages/tad.ai/_components/Upload'

import {
  defaultSupportAudio,
  maxUploadAudioSize,
} from '@/pages/tad.ai/_constant'
import Upload from '@/pages/tad.ai/_components/Upload'


import { useState } from 'react';
import { cls } from 'tagged-classnames-free';
type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

interface FileValue {
  url: string
  filename: string
  fileSize?: number
}
interface UploadAudioCardProps {
  className?: string
  /** 支持的格式, 比如 ['.mp3', '.wav'] */
  acceptList?: string[]
  /** 最大上传音频大小, 单位 MB */
  maxUploadSize?: number
  children: React.ReactNode
  onBeforeUpload?: (file?: FileType) => boolean | Promise<boolean> | void
  onUploadError?: () => void
  onUploading?: () => void
  onUploadStart?: (abortController: AbortController) => void
  onUploadSuccess?: (value: FileValue) => void
  /** 是否抑制取消上传的错误消息 */
  suppressAbortMessage?: boolean
}
function UploadAudioCard(props: UploadAudioCardProps) {
  const {
    className = '',
    acceptList = defaultSupportAudio,
    maxUploadSize = maxUploadAudioSize,
    children,
    onBeforeUpload,
    onUploadError,
    onUploading,
    onUploadStart,
    onUploadSuccess,
    suppressAbortMessage = false,
  } = props
  const { t } = useLingui()
  const [fileList, setFileList] = useState<UploadFile<IFileResponse>[]>([])

  const beforeUpload = async (file: FileType) => {
    // 重置之前数据
    setFileList([])
    const res = await onBeforeUpload?.(file)
    if (res === false) {
      return AntdUpload.LIST_IGNORE
    }

    // 格式
    const isVerifiedType = acceptList?.some((type) =>
      file.name.toLowerCase().endsWith(type),
    )
    if (acceptList && !isVerifiedType) {
      const formats = acceptList.map((f) => f.toUpperCase()).join(' / ')
      message.error(
        t`Unsupported format. Please upload the audio in ${formats} format.`,
      )
      return AntdUpload.LIST_IGNORE
    }

    // 大小
    const overFileLimit =
      maxUploadSize && file.size / 1024 / 1024 > maxUploadSize
    if (overFileLimit) {
      message.error(
        t`Please ensure that your uploaded audio file does not exceed ${maxUploadSize}MB.`,
      )
      return AntdUpload.LIST_IGNORE
    }

    return true
  }

  const handleUploadChange: UploadProps['onChange'] = async (
    info: UploadChangeParam<UploadFile<IFileResponse>>,
  ) => {
    if (info.file.status === 'error') {
      message.error(info.file.error.message)
      setFileList([])
      onUploadError?.()
      return
    }

    setFileList([...info.fileList])
    if (info.file.status === 'uploading') {
      onUploading?.()
      return
    }
    if (info.file.status === 'done') {
      const filename = info.file.response?.filename || ''
      const url = info.file.response?.url || ''
      const fileSize = info.file.size || 0
      onUploadSuccess?.({
        url,
        filename,
        fileSize,
      })
    }
  }

  return (
    <Upload
      accept='audio/*'
      beforeUpload={beforeUpload}
      onChange={handleUploadChange}
      onUploadStart={onUploadStart}
      suppressAbortMessage={suppressAbortMessage}
      className={cls`
        bg-f-bg-base text-f-text-tertiary [&_.coco-upload-drag]:bg-f-bg-base flex size-full flex-col
        items-center justify-center rounded-lg
        [&_.coco-upload-btn]:!flex [&_.coco-upload-btn]:justify-center
        [&_.coco-upload-drag-container]:size-full [&_.coco-upload-drag]:border-0
        [&_.coco-upload]:!p-0
        ${className}
      `}
      fileList={fileList}
      showUploadList={false}
      maxCount={1}
      multiple={false}
      uploadType='audio'
    >
      {children}
    </Upload>
  )
}

export default UploadAudioCard
